// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { formatTimestamp, saveAnnotationsToStorage } from './pdfUtils';
import { apiPost } from '../utils/apiClient';
import { logger } from '../utils/logger';
import './CriticalSummaryPanel.css';

interface Annotation {
  id: string;
  subType?: string;
  isDeleted?: boolean;
  timestamp: string;
  answer?: string;
  text?: string;
  sessionName?: string;
  conversationHistory?: any;
  [key: string]: any;
}

interface PdfInfo {
  fingerprint: string;
  [key: string]: any;
}

interface SocraticMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface CriticalSummaryPanelProps {
  show: boolean;
  annotations: Record<string, Annotation>;
  pdfInfo: PdfInfo | null;
  onClose: () => void;
  onDelete?: (summaryId: string) => Promise<void>;
  onStartSocraticDialog?: () => void;
}

/**
 * 批判性总结管理面板
 */
export default function CriticalSummaryPanel({ show, annotations, pdfInfo, onClose, onDelete, onStartSocraticDialog }: CriticalSummaryPanelProps) {
  const [selectedSummary, setSelectedSummary] = useState<Annotation | null>(null);
  const [showSocraticDialog, setShowSocraticDialog] = useState<boolean>(false);
  const [socraticHistory, setSocraticHistory] = useState<SocraticMessage[]>([]);
  const [isAIThinking, setIsAIThinking] = useState<boolean>(false);

  // 语音生成状态
  const [isGeneratingSpeech, setIsGeneratingSpeech] = useState<boolean>(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const audioCache = React.useRef<Map<string, string>>(new Map());

  // 提取所有批判性总结
  const criticalSummaries = useMemo(() => {
    return Object.values(annotations)
      .filter(ann => ann.subType === 'guide.critical_summary' && !ann.isDeleted)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [annotations]);

  // 当面板打开时，默认选中第一个总结
  useEffect(() => {
    if (show && criticalSummaries.length > 0 && !selectedSummary) {
      setSelectedSummary(criticalSummaries[0]);
    }
  }, [show, criticalSummaries, selectedSummary]);

  // 关闭面板时重置选中
  useEffect(() => {
    if (!show) {
      setSelectedSummary(null);
      setShowSocraticDialog(false);
      setSocraticHistory([]);
    }
  }, [show]);

  if (!show) return null;

  const handleDelete = async (summaryId: string) => {
    if (!window.confirm('确定要删除这个批判性总结吗？')) return;

    if (onDelete) {
      await onDelete(summaryId);
      if (selectedSummary?.id === summaryId) {
        setSelectedSummary(null);
      }
    }
  };

  // 开始苏格拉底对话
  const handleStartSocraticDialog = async () => {
    if (!selectedSummary) return;

    setShowSocraticDialog(true);
    setIsAIThinking(true);

    try {
      const summaryContent = selectedSummary.answer || selectedSummary.text || '';

      const result = await apiPost<any>('/api/guide/socratic-dialogue', {
        context: summaryContent,
        conversationHistory: [],
        pdf_fingerprint: pdfInfo?.fingerprint || null,
      });

      if (result.success) {
        setSocraticHistory([
          { role: 'assistant', content: result.response }
        ]);
      } else {
        alert('AI导师启动失败: ' + (result.error || '未知错误'));
        setShowSocraticDialog(false);
      }
    } catch (error: any) {
      console.error('启动苏格拉底对话失败:', error);
      alert('AI导师启动失败');
      setShowSocraticDialog(false);
    } finally {
      setIsAIThinking(false);
    }
  };

  // 处理用户回答
  const handleUserAnswer = async (answer: string) => {
    if (!answer.trim() || isAIThinking) return;

    const newHistory: SocraticMessage[] = [...socraticHistory, { role: 'user', content: answer }];
    setSocraticHistory(newHistory);
    setIsAIThinking(true);

    try {
      const summaryContent = selectedSummary?.answer || selectedSummary?.text || '';

      const result = await apiPost<any>('/api/guide/socratic-dialogue', {
        context: summaryContent,
        conversationHistory: newHistory,
        pdf_fingerprint: pdfInfo?.fingerprint || null,
      });

      if (result.success) {
        setSocraticHistory([
          ...newHistory,
          { role: 'assistant', content: result.response }
        ]);
      } else {
        alert('获取AI回复失败: ' + (result.error || '未知错误'));
      }
    } catch (error: any) {
      console.error('苏格拉底对话失败:', error);
      alert('获取AI回复失败');
    } finally {
      setIsAIThinking(false);
    }
  };

  // 保存苏格拉底对话到数据库
  const handleSaveSocraticDialog = async () => {
    if (!selectedSummary || socraticHistory.length === 0) return;

    try {
      const userRounds = socraticHistory.filter(m => m.role === 'user').length;
      const dialogTitle = `苏格拉底对话_${userRounds}轮_${new Date().toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;

      const newAnnotation = {
        id: `socratic_dialogue_${Date.now()}`,
        pdfFingerprint: pdfInfo!.fingerprint,
        page: 0,
        text: '',
        question: `苏格拉底对话：${dialogTitle}`,
        answer: socraticHistory.map(msg => `**${msg.role === 'user' ? '你' : 'AI导师'}**：${msg.content}`).join('\n\n'),
        tag: 'dialogue',
        timestamp: new Date().toISOString(),
        sessionType: 'guide',
        subType: 'guide.socratic_dialogue',
        sessionName: dialogTitle,
        type: 'text',
        conversationHistory: {
          dialogHistory: socraticHistory,
          baseCriticalSummaryId: selectedSummary.id,
          completedAt: new Date().toISOString()
        },
        parentAnnotationId: selectedSummary.id
      };

      // 使用新的 blocks API 保存
      const result = await saveAnnotationsToStorage(pdfInfo!.fingerprint, { [newAnnotation.id]: newAnnotation } as any);

      if (result) {
        alert('苏格拉底对话已保存！');
        setShowSocraticDialog(false);
        setSocraticHistory([]);
      } else {
        alert('保存失败');
      }
    } catch (error: any) {
      console.error('保存苏格拉底对话失败:', error);
      alert('保存失败: ' + error.message);
    }
  };

  // 关闭苏格拉底对话
  const handleCloseSocraticDialog = () => {
    if (socraticHistory.length > 0) {
      const confirmed = window.confirm('要保存这次对话吗？\n\n点击"确定"保存后关闭\n点击"取消"直接关闭（对话将丢失）');
      if (confirmed) {
        handleSaveSocraticDialog();
        return;
      }
    }
    setShowSocraticDialog(false);
    setSocraticHistory([]);
  };

  // 复制总结内容到剪贴板
  const handleCopySummary = async () => {
    if (!selectedSummary) return;
    try {
      await navigator.clipboard.writeText(selectedSummary.answer || selectedSummary.text || '');
      alert('已复制到剪贴板');
    } catch (error: any) {
      console.error('复制失败:', error);
      alert('复制失败');
    }
  };

  // 生成语音
  const handleGenerateAudio = async () => {
    if (!selectedSummary) return;

    const text = selectedSummary.answer || selectedSummary.text || '';
    if (!text || text.trim().length === 0) {
      alert('没有可转换的文本内容');
      return;
    }

    const textKey = text.trim();

    // 停止当前播放
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }

    // 检查缓存
    if (audioCache.current.has(textKey)) {
      try {
        const cachedUrl = audioCache.current.get(textKey)!;
        const audio = new Audio(cachedUrl);
        audio.onplay = () => setCurrentAudio(audio);
        audio.onended = () => setCurrentAudio(null);
        audio.onerror = () => {
          URL.revokeObjectURL(cachedUrl);
          audioCache.current.delete(textKey);
          handleGenerateAudio();
        };
        await audio.play();
        return;
      } catch (error: any) {
        logger.log('缓存音频播放失败，重新生成');
      }
    }

    setIsGeneratingSpeech(true);

    try {
      const response = await fetch('/api/generate-speech-stream', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: textKey }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('音频数据为空');
      }

      const audioBlob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);

      // 缓存管理
      if (audioCache.current.size >= 20) {
        const firstKey = audioCache.current.keys().next().value;
        if (firstKey) {
          const oldUrl = audioCache.current.get(firstKey)!;
          URL.revokeObjectURL(oldUrl);
          audioCache.current.delete(firstKey);
        }
      }
      audioCache.current.set(textKey, audioUrl);

      const audio = new Audio(audioUrl);
      audio.oncanplay = () => setIsGeneratingSpeech(false);
      audio.onplay = () => setCurrentAudio(audio);
      audio.onended = () => setCurrentAudio(null);
      audio.onerror = () => {
        alert('音频播放失败');
        setCurrentAudio(null);
        setIsGeneratingSpeech(false);
      };

      await audio.play();
    } catch (error: any) {
      console.error('语音生成失败:', error);
      alert('语音生成失败: ' + error.message);
      setIsGeneratingSpeech(false);
    }
  };

  // 停止音频播放
  const handleStopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
  };

  return (
    <>
      <div className="critical-summary-overlay" onClick={onClose}></div>
      <div className="critical-summary-panel">
        {/* 头部 */}
        <div className="panel-header">
          <h3>批判性总结</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* 内容区域 */}
        <div className="panel-content">
          {criticalSummaries.length === 0 ? (
            <div className="critical-summary-empty-state">
              <p className="empty-icon">&#128221;</p>
              <p className="empty-text">还没有批判性总结</p>
              <p className="empty-hint">
                在标注列表的「导读记录」中，选择至少2个导读后，点击「生成批判性总结」按钮
              </p>
            </div>
          ) : (
            <div className="critical-summary-layout">
              {/* 左侧：总结列表 */}
              <div className="critical-summary-list">
                <div className="list-header">
                  <span className="count-badge">{criticalSummaries.length} 个总结</span>
                </div>
                {criticalSummaries.map(summary => (
                  <div
                    key={summary.id}
                    className={`critical-summary-card ${selectedSummary?.id === summary.id ? 'active' : ''}`}
                    onClick={() => setSelectedSummary(summary)}
                  >
                    <div className="card-title">{summary.sessionName || '未命名总结'}</div>
                    <div className="card-meta">
                      <span className="time">{formatTimestamp(summary.timestamp)}</span>
                      <span className="source-count">
                        基于 {summary.conversationHistory?.baseAnnotationIds?.length || 0} 个导读
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* 右侧：总结详情 */}
              <div className="critical-summary-detail">
                {selectedSummary ? (
                  <>
                    <div className="detail-header">
                      <h4>{selectedSummary.sessionName || '未命名总结'}</h4>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(selectedSummary.id)}
                      >
                        删除
                      </button>
                    </div>
                    <div className="detail-meta">
                      <span>创建时间：{formatTimestamp(selectedSummary.timestamp)}</span>
                      <span>基于导读：{selectedSummary.conversationHistory?.baseAnnotationIds?.length || 0} 个</span>
                    </div>

                    {/* 复制和语音按钮 */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                      <button
                        onClick={handleCopySummary}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="复制到剪贴板"
                      >
                        复制
                      </button>
                      <button
                        onClick={handleGenerateAudio}
                        disabled={isGeneratingSpeech}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          cursor: isGeneratingSpeech ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          opacity: isGeneratingSpeech ? 0.6 : 1
                        }}
                        title="转为语音"
                      >
                        {isGeneratingSpeech ? '生成中...' : '🔊 语音'}
                      </button>
                      {currentAudio && (
                        <button
                          onClick={handleStopAudio}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="停止播放"
                        >
                          ⏹ 停止
                        </button>
                      )}
                    </div>

                    <div className="detail-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex]}
                      >
                        {selectedSummary.answer || selectedSummary.text || '无内容'}
                      </ReactMarkdown>
                    </div>

                    {/* AI导师提问按钮 */}
                    <div className="detail-actions">
                      <button
                        className="socratic-btn"
                        onClick={handleStartSocraticDialog}
                        disabled={showSocraticDialog}
                      >
                        AI导师深度提问
                      </button>
                      <p className="action-hint">通过苏格拉底式提问，引导你深入思考总结内容</p>
                    </div>
                  </>
                ) : (
                  <div className="critical-summary-no-selection">
                    <p>&larr; 选择一个总结查看详情</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 苏格拉底对话面板 */}
      {showSocraticDialog && (
        <div className="socratic-overlay" onClick={handleCloseSocraticDialog}>
          <div className="socratic-panel" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="socratic-header">
              <h4>AI导师引导对话</h4>
              <button onClick={handleCloseSocraticDialog} className="close-btn">&times;</button>
            </div>

            <div className="socratic-content">
              {socraticHistory.map((msg, index) => (
                <div
                  key={index}
                  className={`socratic-message ${msg.role === 'user' ? 'user-message' : 'ai-message'}`}
                >
                  <strong>{msg.role === 'user' ? '你' : 'AI导师'}：</strong>
                  <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {msg.content}
                  </ReactMarkdown>
                </div>
              ))}

              {isAIThinking && (
                <div className="ai-thinking">
                  <span className="thinking-dots">AI导师思考中</span>
                </div>
              )}
            </div>

            <div className="socratic-footer">
              {socraticHistory.length < 10 ? (
                <>
                  <textarea
                    placeholder="输入你的思考和回答..."
                    onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        handleUserAnswer((e.target as HTMLTextAreaElement).value);
                        (e.target as HTMLTextAreaElement).value = '';
                      }
                    }}
                    disabled={isAIThinking}
                    className="socratic-input"
                  />
                  <button
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      const textarea = (e.target as HTMLElement).previousSibling as HTMLTextAreaElement;
                      if (textarea.value.trim()) {
                        handleUserAnswer(textarea.value);
                        textarea.value = '';
                      }
                    }}
                    disabled={isAIThinking}
                    className="submit-btn"
                  >
                    {isAIThinking ? '思考中...' : '提交回答 (Ctrl+Enter)'}
                  </button>
                  <p className="dialog-progress">对话进度：{socraticHistory.filter(m => m.role === 'user').length}/5 轮</p>
                </>
              ) : (
                <div className="dialog-complete">
                  <p>对话已完成！你已经完成了5轮深度思考。</p>
                  <button
                    onClick={handleSaveSocraticDialog}
                    className="submit-btn"
                    style={{ marginTop: '12px', width: '100%' }}
                  >
                    保存对话记录
                  </button>
                  <p className="hint" style={{ marginTop: '8px' }}>对话将保存到导读记录中，可随时查看</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
